import React, { useState, useEffect } from 'react';

interface TagInput {
  key: string;
  value: string;
}

interface TagTemplate {
  id: string;
  name: string;
  tags: TagInput[];
  createdAt: string;
}

interface Props {
  currentTags: TagInput[];
  onLoadTemplate: (tags: TagInput[]) => void;
}

const STORAGE_KEY = 'atm_tag_templates';

const TagTemplatesPanel: React.FC<Props> = ({ currentTags, onLoadTemplate }) => {
  const [templates, setTemplates] = useState<TagTemplate[]>([]);
  const [newName, setNewName] = useState('');
  const [showSave, setShowSave] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : null;
      if (Array.isArray(parsed)) setTemplates(parsed);
    } catch {
      // Corrupt stored templates — start fresh rather than crashing the panel.
    }
  }, []);

  const saveTemplates = (updated: TagTemplate[]) => {
    setTemplates(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const saveTemplate = () => {
    if (!newName.trim()) return;
    const validTags = currentTags.filter(t => t.key && t.value);
    if (validTags.length === 0) return;
    const updated = [...templates, { id: Date.now().toString(), name: newName.trim(), tags: validTags, createdAt: new Date().toISOString() }];
    saveTemplates(updated);
    setNewName('');
    setShowSave(false);
  };

  const deleteTemplate = (id: string) => {
    saveTemplates(templates.filter(t => t.id !== id));
  };

  return (
    <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: '8px', padding: '1.25rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3 style={{ margin: 0, color: '#f0f0f0', fontSize: '1rem' }}>Tag Templates</h3>
        <button
          onClick={() => setShowSave(!showSave)}
          style={{ padding: '0.35rem 0.75rem', background: '#007bff', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
        >
          + Save Current
        </button>
      </div>

      {showSave && (
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Template name (e.g. Production Tags)"
            style={{ flex: 1, background: '#1a1a1e', color: '#f0f0f0', border: '1px solid #555', borderRadius: '6px', padding: '0.5rem 0.75rem' }}
            onKeyDown={e => e.key === 'Enter' && saveTemplate()}
          />
          <button onClick={saveTemplate} style={{ padding: '0.5rem 1rem', background: '#28a745', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Save</button>
          <button onClick={() => setShowSave(false)} style={{ padding: '0.5rem 0.75rem', background: '#444', color: '#f0f0f0', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>&#x2715;</button>
        </div>
      )}

      {templates.length === 0 ? (
        <p style={{ color: '#888', fontSize: '0.875rem', margin: 0 }}>No templates saved yet. Enter tags above and save them as a template.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {templates.map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#1a1a1e', borderRadius: '6px', padding: '0.6rem 0.85rem', border: '1px solid #333' }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#f0f0f0', fontWeight: 500, fontSize: '0.9rem' }}>{t.name}</div>
                <div style={{ color: '#888', fontSize: '0.78rem', marginTop: '0.15rem' }}>
                  {t.tags.map(tag => `${tag.key}=${tag.value}`).join(' · ')}
                </div>
              </div>
              <button
                onClick={() => onLoadTemplate(t.tags)}
                style={{ padding: '0.35rem 0.75rem', background: '#007bff22', color: '#4da6ff', border: '1px solid #007bff44', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                Load
              </button>
              <button
                onClick={() => deleteTemplate(t.id)}
                style={{ padding: '0.35rem 0.5rem', background: 'transparent', color: '#888', border: 'none', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                &#x2715;
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagTemplatesPanel;
